/**
 * Customers — the customer control plane, over the real directory.
 *
 * ===========================================================================
 * THE ADMISSION DECISION IS MADE HERE TOO, AND THAT IS NOT REDUNDANT
 * ===========================================================================
 * `App.tsx` already refuses a principal that is not a platform operator. This
 * component asks again, on its own, before it renders one character about a
 * customer organization. Two reasons, and neither is belt-and-braces:
 *
 *   1. This is the surface where tenant authority leaking into platform
 *      authority would first be VISIBLE to a human — a customer list opening
 *      because somebody administers a customer. The refusal belongs at the
 *      thing being protected, not only at the front door.
 *   2. It is the only way the guard can be proved. `apps/console`'s front door
 *      today produces exactly one kind of principal, so a fixture aimed at
 *      `App` can never construct a tenant session in the way. Rendering THIS
 *      component with an org_admin session is a fixture in which this branch is
 *      the sole thing standing in the way — which the lane contract says is the
 *      only proof a guard works.
 *
 * ===========================================================================
 * THE LIST IS THE SERVER'S, AND THE IN-MEMORY ONE IS GONE
 * ===========================================================================
 * Until `GET /api/system/customers` existed this screen showed the replies to
 * each provisioning call made in the tab, labelled as not being the customer
 * estate. That list has been REMOVED rather than kept alongside the real one: a
 * console holding both would have two answers to "which customers are there",
 * and the remembered one never goes stale because nothing ever contradicts it.
 * That is the second canonical state this whole surface exists to avoid.
 *
 * A create now REFRESHES THE DIRECTORY. The receipt is kept for exactly the
 * fields the directory does not carry — the first administrator's invited
 * state, the invitation expiry, the delivery job, the mail provider — and it is
 * rendered on the detail page as a reading of one instant with that instant
 * printed beside it.
 *
 * ===========================================================================
 * WHAT AN EMPTY LIST MEANS HERE
 * ===========================================================================
 * `customers: []` with `count: 0` from a 200 is a MEASURED ZERO and renders as
 * one, in words. Every other way of not having rows — refused, not accepted,
 * malformed, unreachable, not yet answered — renders as itself. The table is
 * never drawn empty to stand in for any of them.
 */
import { useCallback, useEffect, useState, useMemo } from 'react';
import { isStaffToken } from '../platform/staffSession.js';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import { areaFor, consoleAdmission } from '../platform/authority.js';
import type { PresentedPrincipal } from '../platform/authority.js';
import { describeMeasure, failureMeasure } from '../platform/customers.js';
import {
  PLATFORM_READS,
  customers as customersRequest,
  jobsHealth,
  pendingReads,
} from '../platform/tenancy.js';
import type {
  CreatedCustomer,
  CustomerDirectory,
  CustomerDirectoryEntry,
} from '../platform/tenancy.js';
import { describeJobsHealth } from '../platform/jobsHealth.js';
import { JobsHealthPanel } from './JobsHealthPanel.js';
import type { JobsHealthBody, JobsHealthReading } from '../platform/jobsHealth.js';
import { AddCustomer } from './AddCustomer.js';
import { CustomerDetailLoader } from './CustomerDetail.js';
import type { ProvisioningReceipt } from './CustomerDetail.js';

/** `null` is "the read has not answered", which is not "there are none". */
export type DirectoryOutcome = ConsoleOutcome<CustomerDirectory> | null;

/**
 * The directory, in every state it can be in. Exported so it can be rendered
 * directly with each outcome — an effect can only be tested in the state it
 * happens to reach, and the refusals are the states that matter.
 */
export function CustomerDirectoryList({
  directory,
  selectedId,
  onSelect,
}: {
  directory: DirectoryOutcome;
  selectedId: string | null;
  onSelect: (customer: CustomerDirectoryEntry) => void;
}) {
  if (directory === null) {
    return <p className="quiet">Reading GET /api/system/customers…</p>;
  }

  if (directory.kind !== 'ok') {
    return (
      <div className="banner" role="alert">
        <strong>The customer directory did not answer.</strong>
        <p>{describeMeasure(failureMeasure(directory))}</p>
        <p className="quiet">
          No list is being shown as empty on account of this. This is not "there are no customers" —
          it is a read that did not complete, and the two lead to opposite decisions.
        </p>
      </div>
    );
  }

  const rows = directory.value.customers ?? [];
  const count = directory.value.count;

  return (
    <>
      <p className="quiet">
        {rows.length === 0 ? (
          <>
            The directory answered and returned <strong>zero customers</strong>. That is a measured
            zero: the platform read completed and there are none.
          </>
        ) : (
          <>
            {count} customer{count === 1 ? '' : 's'}, newest first.
          </>
        )}
      </p>

      {/* TWO NUMBERS THAT CAN DISAGREE IS HOW A CONSOLE RENDERS "3 CUSTOMERS"
          OVER A LIST OF TWO. The server computes `count` as `customers.length`,
          so a disagreement means the shape changed under us and it says so
          rather than quietly preferring one. */}
      {count !== rows.length ? (
        <div className="banner" role="alert">
          <strong>The directory disagrees with itself.</strong> It reported a count of {count} and
          sent {rows.length} row{rows.length === 1 ? '' : 's'}. Neither number is being trusted.
        </div>
      ) : null}

      {rows.map((customer) => (
        <div
          key={customer.id}
          className="card"
          data-selected={customer.id === selectedId ? 'true' : 'false'}
        >
          <h3>{customer.name}</h3>
          <p className="quiet">
            <code>{customer.id}</code> · slug <code>{customer.slug}</code> · {customer.timezone} ·
            created {customer.createdAt}
          </p>
          <p>
            Status: <strong>{customer.status}</strong>
            {customer.status === 'active' ? null : (
              <>
                {' '}
                — listed deliberately. A directory that omitted it would answer "no such
                organization" for the customer an operator is most likely looking for.
              </>
            )}
          </p>
          <p>
            <button type="button" onClick={() => onSelect(customer)}>
              Open
            </button>
          </p>
        </div>
      ))}
    </>
  );
}

export function CustomersArea({
  presented,
  client,
}: {
  presented: PresentedPrincipal;
  client: ConsoleClient;
}) {
  // MEMOIZED (found by the full-Console check, 2026-10-09): consoleAdmission
  // returns a new object each render, which made `actor` new each render, the
  // load callbacks new each render, and the directory effect re-request the API
  // in an endless loop while the screen said "Reading…".
  const admission = useMemo(() => consoleAdmission(presented), [presented]);
  const area = areaFor('customers');

  const [directory, setDirectory] = useState<DirectoryOutcome>(null);
  const [selected, setSelected] = useState<CustomerDirectoryEntry | null>(null);
  /**
   * Receipts are keyed by organization id and hold ONLY what the directory
   * cannot carry. They are never a list of customers and are never consulted to
   * answer "which customers exist" — see the module docblock.
   */
  const [receipts, setReceipts] = useState<Record<string, ProvisioningReceipt>>({});
  /** `null` is "the read has not answered", which is not "the queues are fine". */
  const [health, setHealth] = useState<JobsHealthReading | null>(null);

  const actor = admission.admitted ? admission.actor : null;

  const loadHealth = useCallback(async () => {
    if (!actor) return;
    // The verdict is IN THE BODY on this route — it always answers 200,
    // deliberately, so that "the queue is blocked" cannot be mistaken for "the
    // endpoint is down". Reading it is `platform/jobsHealth.ts`'s job: this
    // screen used to keep only the roll-up word, which is how a failing queue
    // reached an operator as the single character string "error" and nothing
    // that said which queue or what to do about it (PH-ATT-005).
    setHealth(describeJobsHealth(await client<JobsHealthBody>(jobsHealth(), actor)));
  }, [actor, client]);

  const loadDirectory = useCallback(async () => {
    if (!actor) return;
    setDirectory(null);
    setDirectory(await client<CustomerDirectory>(customersRequest(), actor));
  }, [actor, client]);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    void loadDirectory();
  }, [loadDirectory]);

  if (!admission.admitted) {
    return (
      <div className="banner" role="alert">
        <strong>Refused — {admission.reason.replace(/_/g, ' ')}.</strong>
        <p>{admission.message}</p>
      </div>
    );
  }

  return (
    <>
      <p className="quiet">
        This area needs <code>{area.capability}</code>
        {isStaffToken(admission.actor.systemToken) ? (
          <>, held by your staff role. A 403 below means your role does not include it.</>
        ) : (
          <>
            {' '}
            through the <code>{area.credentialEnvVar}</code> operator credential. If the platform
            API answers 403 below, the credential you presented is scoped to another authority.
          </>
        )}
      </p>

      <JobsHealthPanel health={health} />

      <AddCustomer
        actor={admission.actor}
        client={client}
        onCreated={(created: CreatedCustomer, measuredAt: string) => {
          setReceipts((prev) => ({ ...prev, [created.orgId]: { created, measuredAt } }));
          // RE-READ, do not append. The directory is the answer to "which
          // customers exist" and a locally appended row would be a second one.
          void loadDirectory();
        }}
      />

      <section>
        <h2>Customers</h2>
        <p className="quiet">
          <code>GET /api/system/customers</code> — the six identity columns of the tenant root.
          Deliberately no user count, no last-activity timestamp and no "health": each would be a
          cross-tenant read of tenant business data dressed as an operator convenience. Onboarding
          progress is per customer and is on the detail page, one read per customer opened.
        </p>
        <p>
          <button type="button" onClick={() => void loadDirectory()}>
            Re-read the directory
          </button>
        </p>
        <CustomerDirectoryList
          directory={directory}
          selectedId={selected?.id ?? null}
          onSelect={setSelected}
        />
      </section>

      {selected ? (
        <CustomerDetailLoader
          key={selected.id}
          customer={selected}
          actor={admission.actor}
          client={client}
          receipt={receipts[selected.id]}
        />
      ) : null}

      <section>
        <h2>What this control plane cannot answer yet</h2>
        <p className="quiet">
          {pendingReads().length} of {PLATFORM_READS.length} declared platform reads are not
          registered by the API. Each row is a question this console is deliberately not answering
          rather than answering with a zero.
        </p>
        <ul>
          {pendingReads().map((read) => (
            <li key={read.id}>
              <code>
                {read.method} {read.route}
              </code>{' '}
              — {read.label}. Would supply: {read.supplies.join('; ')}.{' '}
              <span className="quiet">Held by: {read.heldBy}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
